import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { Component, Input, OnChanges, OnInit, signal } from '@angular/core';
import { RecentBookingDto } from '@buspro/web/apis';

@Component({
  selector: 'buspro-admin-recent-bookings-table',
  standalone: true,
  imports: [CommonModule, CurrencyPipe, DatePipe],
  template: `
    <div class="card bg-white shadow-md rounded-lg">
      <div class="card-body p-6">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-semibold">Recent Bookings</h3>
          @if (limit && bookings.length > limit) {
            <span class="text-sm text-gray-500">Showing {{ limit }} of {{ bookings.length }}</span>
          }
        </div>

        @if (bookings.length === 0) {
          <div class="text-center py-8 text-gray-400">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-16 h-16 mx-auto mb-4 text-gray-300"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 00-1.883 2.542l.857 6a2.25 2.25 0 002.227 1.932H19.05a2.25 2.25 0 002.227-1.932l.857-6a2.25 2.25 0 00-1.883-2.542m-16.5 0V6A2.25 2.25 0 016 3.75h3.879a1.5 1.5 0 011.06.44l2.122 2.12a1.5 1.5 0 001.06.44H18A2.25 2.25 0 0120.25 9v.776"
              />
            </svg>
            <p class="mt-2">No recent bookings</p>
          </div>
        } @else {
          <div class="overflow-x-auto">
            <table class="table table-zebra table-sm w-full">
              <thead>
                <tr>
                  <th class="cursor-pointer hover:bg-gray-100" (click)="sortBy('passengerName')">
                    Passenger
                    @if (sortColumn() === 'passengerName') {
                      <span class="ml-1">{{ sortDirection() === 'asc' ? '↑' : '↓' }}</span>
                    }
                  </th>
                  <th class="cursor-pointer hover:bg-gray-100" (click)="sortBy('seatNumber')">
                    Seat
                    @if (sortColumn() === 'seatNumber') {
                      <span class="ml-1">{{ sortDirection() === 'asc' ? '↑' : '↓' }}</span>
                    }
                  </th>
                  <th class="cursor-pointer hover:bg-gray-100" (click)="sortBy('fare')">
                    Fare
                    @if (sortColumn() === 'fare') {
                      <span class="ml-1">{{ sortDirection() === 'asc' ? '↑' : '↓' }}</span>
                    }
                  </th>
                  <th class="cursor-pointer hover:bg-gray-100" (click)="sortBy('bookedAt')">
                    Time
                    @if (sortColumn() === 'bookedAt') {
                      <span class="ml-1">{{ sortDirection() === 'asc' ? '↑' : '↓' }}</span>
                    }
                  </th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                @for (booking of sortedBookings(); track booking.id) {
                  <tr class="hover:bg-gray-50 cursor-pointer" (click)="onBookingClick(booking)">
                    <td class="font-medium">{{ booking.passengerName }}</td>
                    <td>{{ booking.seatNumber }}</td>
                    <td>{{ booking.fare | currency:'UGX':'symbol':'1.0-0' }}</td>
                    <td class="text-xs text-gray-500">{{ booking.bookedAt | date:'short' }}</td>
                    <td>
                      <span
                        class="badge badge-sm"
                        [class.badge-success]="booking.status === 'CONFIRMED'"
                        [class.badge-warning]="booking.status === 'PENDING'"
                        [class.badge-error]="booking.status === 'CANCELLED'"
                      >
                        {{ booking.status }}
                      </span>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </div>
    </div>
  `,
})
export class RecentBookingsTableComponent implements OnInit, OnChanges {
  @Input() bookings: RecentBookingDto[] = [];
  @Input() limit?: number;

  sortColumn = signal<keyof RecentBookingDto | null>(null);
  sortDirection = signal<'asc' | 'desc'>('desc');

  sortedBookings = signal<RecentBookingDto[]>([]);

  ngOnInit(): void {
    this.updateSortedBookings();
  }

  ngOnChanges(): void {
    this.updateSortedBookings();
  }

  sortBy(column: keyof RecentBookingDto): void {
    if (this.sortColumn() === column) {
      this.sortDirection.set(this.sortDirection() === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortColumn.set(column);
      this.sortDirection.set('asc');
    }
    this.updateSortedBookings();
  }

  private updateSortedBookings(): void {
    let sorted = [...this.bookings];

    const sortCol = this.sortColumn();
    if (sortCol) {
      sorted.sort((a, b) => {
        const aVal = a[sortCol];
        const bVal = b[sortCol];

        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        if (typeof aVal === 'string' && typeof bVal === 'string') {
          // Handle date strings (bookedAt is a string)
          if (sortCol === 'bookedAt') {
            const aDate = new Date(aVal);
            const bDate = new Date(bVal);
            if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
              return this.sortDirection() === 'asc'
                ? aDate.getTime() - bDate.getTime()
                : bDate.getTime() - aDate.getTime();
            }
          }
          return this.sortDirection() === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        }

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return this.sortDirection() === 'asc' ? aVal - bVal : bVal - aVal;
        }

        return 0;
      });
    } else {
      // Default: sort by bookedAt descending
      sorted.sort((a, b) => {
        const aDate = new Date(a.bookedAt);
        const bDate = new Date(b.bookedAt);
        return bDate.getTime() - aDate.getTime();
      });
    }

    if (this.limit) {
      sorted = sorted.slice(0, this.limit);
    }

    this.sortedBookings.set(sorted);
  }

  onBookingClick(_booking: RecentBookingDto): void {
    // Emit event or navigate to booking details
    // For now, just a placeholder
  }
}
